import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  InboxArrowDownIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline"

// Canonical, always-source-of-truth doc. This page is a pointer + the actionable
// how-to; deep details (secrets table, DNS/DMARC) live in the markdown so the UI
// can't drift.
const DOC_URL =
  "https://github.com/taitsmp/fb-clips-cf-monorepo/blob/master/backend/docs/email-sending.md"
const EXEC_PLAN_URL =
  "https://github.com/taitsmp/fb-clips-cf-monorepo/blob/master/docs/email-admin-and-deliverability-exec-plan.md"

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs text-primary">
      {children}
    </code>
  )
}

const SMTP_SETTINGS: Array<{ field: string; value: string }> = [
  { field: "SMTP server", value: "smtp.resend.com" },
  { field: "Port", value: "465" },
  { field: "Username", value: "resend" },
  { field: "Password", value: "your Resend send-only key (re_…)" },
  { field: "Connection", value: "SSL (secured)" },
]

const LANES: Array<{ lane: string; whatFor: string; from: string }> = [
  {
    lane: "Inbound",
    whatFor: "Receiving mail to @playside.co",
    from: "MX → Cloudflare Email Routing → forwards to Gmail",
  },
  {
    lane: "Transactional",
    whatFor: "App mail (invites, exports, trades…)",
    from: "noreply@playside.co via Amazon SES",
  },
  {
    lane: "Human replies",
    whatFor: "You, replying to coaches as yourself",
    from: "tait@ / tait.larson@playside.co via Resend SMTP",
  },
]

export function EmailSetup() {
  return (
    <div className="space-y-6">
      {/* The how-to — the actionable bit for any teammate */}
      <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
        <div className="flex items-center gap-2">
          <PaperAirplaneIcon className="h-5 w-5 text-secondary" />
          <h2 className="text-lg font-semibold text-primary">
            Send from Gmail as <span className="font-mono">@playside.co</span>
          </h2>
        </div>

        <p className="text-sm text-secondary">
          This lets you reply as <Code>tait@playside.co</Code> (or any <Code>@playside.co</Code>{" "}
          address) from your normal Gmail, with mail that passes SPF/DKIM/DMARC. Sending &quot;as{" "}
          <Code>@playside.co</Code>&quot; straight from Gmail goes through Google&apos;s consumer
          servers, which can&apos;t authenticate for our domain — Yahoo hard-rejects it and
          Outlook/Hotmail junk it. Routing through Resend&apos;s SMTP fixes that. Set this up once
          per address you want to send as.
        </p>

        <div className="rounded-md border border-border bg-background/40 p-3 text-sm text-secondary">
          <span className="font-medium text-primary">You need:</span> a Resend{" "}
          <span className="font-medium text-primary">send-only</span> API key (<Code>re_…</Code>).
          Get one in Resend →{" "}
          <span className="text-primary">
            API Keys → Create API Key → &quot;Sending access&quot;
          </span>
          . The same key can be reused across addresses. This key is pasted into Gmail — it is{" "}
          <span className="font-medium text-primary">not</span> a server secret.
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-secondary">SMTP settings</h3>
          <table className="w-full text-sm">
            <tbody>
              {SMTP_SETTINGS.map((row) => (
                <tr key={row.field} className="border-t border-border/50">
                  <td className="py-1.5 pr-4 font-medium text-secondary">{row.field}</td>
                  <td className="py-1.5 font-mono text-primary">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-secondary">Steps (Gmail web)</h3>
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-secondary">
            <li>
              Gmail → ⚙️{" "}
              <span className="text-primary">
                See all settings → Accounts and Import → &quot;Send mail as&quot; → Add another
                email address
              </span>
              .
            </li>
            <li>
              Name: your name. Email: <Code>tait@playside.co</Code>. → Next Step.
            </li>
            <li>Enter the SMTP settings above, then Add Account.</li>
            <li>
              Gmail emails a confirmation code to that address. Because Cloudflare forwards{" "}
              <Code>@playside.co</Code> mail to your Gmail, the code arrives in your inbox — enter
              it.
            </li>
            <li>
              (Optional) set it as your default From. Repeat for any other address (e.g.{" "}
              <Code>tait.larson@playside.co</Code>).
            </li>
          </ol>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          <CheckCircleIcon className="h-5 w-5 shrink-0" />
          <span>
            <span className="font-medium">Verify it worked:</span> send a test to a Yahoo or
            Outlook/Hotmail address, open it, and use{" "}
            <span className="font-medium">Show original</span> → confirm SPF / DKIM / DMARC all
            PASS. Yahoo is the strict one — if it lands there, you&apos;re good.
          </span>
        </div>
      </section>

      {/* How it works — short architecture summary */}
      <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
        <div className="flex items-center gap-2">
          <InboxArrowDownIcon className="h-5 w-5 text-secondary" />
          <h2 className="text-lg font-semibold text-primary">How Playside email works</h2>
        </div>
        <p className="text-sm text-secondary">
          Three independent lanes, each authenticated as <Code>playside.co</Code> (DKIM-aligned).
          DNS is at Cloudflare.
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-secondary">
              <th className="py-1 pr-4 font-medium">Lane</th>
              <th className="py-1 pr-4 font-medium">What it&apos;s for</th>
              <th className="py-1 font-medium">Path / from</th>
            </tr>
          </thead>
          <tbody>
            {LANES.map((row) => (
              <tr key={row.lane} className="border-t border-border/50 align-top">
                <td className="py-1.5 pr-4 font-medium text-primary">{row.lane}</td>
                <td className="py-1.5 pr-4 text-secondary">{row.whatFor}</td>
                <td className="py-1.5 text-secondary">{row.from}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-sm text-secondary">
          Transactional mail flows: monorepo <Code>email.ts</Code> → email Lambda (API Gateway,{" "}
          <Code>x-api-key</Code>) → Amazon SES (us-east-2) → recipient. The Lambda + Terraform live
          in <Code>fb-clips-video-task-workers</Code>.
        </p>
      </section>

      {/* Full reference links */}
      <section className="rounded-lg border border-border bg-surface p-5 space-y-3">
        <h2 className="text-lg font-semibold text-primary">Full reference</h2>
        <p className="text-sm text-secondary">
          This page is the actionable summary. The complete technical reference — secrets table,
          DNS/DMARC notes, SES identity details — lives in the repo and is the source of truth.
        </p>
        <div className="flex flex-col gap-2">
          <a
            href={DOC_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            backend/docs/email-sending.md
          </a>
          <a
            href={EXEC_PLAN_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            Email admin &amp; deliverability exec plan
          </a>
        </div>
      </section>
    </div>
  )
}
